import { ModuleWithProviders } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { RedirectAuthenticationComponent } from '®app/redirect-authentication/redirect-authentication.component';
import { CurrentUserGuard } from '®shared/current-user';
import { TenantGuard } from '®shared/tenant/tenant-guard.service';

import { ErrorPageComponent } from './error-page';
import { LtiMissingContentComponent } from './lti/lti-missing-content/lti-missing-content.component';
import { PageNotFoundComponent } from './page-not-found';
import { ItemModeResolverComponent } from './reading-lists/item-view/mode-resolver';
import { ListModeResolverComponent } from './reading-lists/list/mode-resolver';
import { SectionModeResolverComponent } from './reading-lists/section-view/mode-resolver';

export const routes: Routes = [
  {
    path: 'error',
    pathMatch: 'full',
    component: ErrorPageComponent,
  },
  {
    path: 'error.html',
    pathMatch: 'full',
    component: ErrorPageComponent,
  },
  {
    path: 'lti-missing-content',
    pathMatch: 'full',
    component: LtiMissingContentComponent,
  },
  {
    path: ':version/:tenantCode/redirect-authentication.html',
    pathMatch: 'full',
    component: RedirectAuthenticationComponent,
  },
  {
    path: ':version/breakout-authentication.html',
    pathMatch: 'full',
    loadChildren: () =>
      import('app/breakout-authentication/breakout-authentication.module').then(
        m => m.BreakoutAuthenticationModule,
      ),
  },
  {
    path: ':version/lti-authentication.html',
    pathMatch: 'full',
    loadChildren: () =>
      import('app/lti/lti-authentication/lti-authentication.module').then(
        m => m.LtiAuthenticationModule,
      ),
  },
  {
    path: ':version/pkce/error.html',
    pathMatch: 'full',
    component: ErrorPageComponent,
  },
  {
    path: ':version/:tenantCode',
    resolve: {
      currentUser: CurrentUserGuard,
    },
    canActivateChild: [CurrentUserGuard],
    children: [
      {
        path: 'lists/create.html',
        canActivateChild: [TenantGuard],
        resolve: {
          tenant: TenantGuard,
        },
        loadChildren: () =>
          import('app/reading-lists/list/create/list-create.module').then(
            m => m.ListCreateModule,
          ),
      },
      // mode-based routing for lists
      {
        path: 'lists/:listId',
        pathMatch: 'full',
        component: ListModeResolverComponent,
      },
      {
        path: 'lists/:listId/modes/edit',
        loadChildren: () =>
          import('app/reading-lists/list/edit/list-edit.module').then(
            m => m.ListEditModule,
          ),
      },
      // all other modes are variations on List View
      {
        path: 'lists/:listId/modes',
        loadChildren: () =>
          import('app/reading-lists/list/view/list-view.module').then(
            m => m.ListViewModule,
          ),
      },
      // mode-based routing for sections
      {
        path: 'sections/:sectionId',
        pathMatch: 'full',
        component: SectionModeResolverComponent,
      },
      {
        path: 'sections/:sectionId/modes',
        loadChildren: () =>
          import('app/reading-lists/section-view/section-view.module').then(
            m => m.SectionViewModule,
          ),
      },
      // mode-based routing for items
      {
        path: 'items/:itemId',
        pathMatch: 'full',
        component: ItemModeResolverComponent,
      },
      {
        path: 'items/:itemId/modes',
        loadChildren: () =>
          import('app/reading-lists/item-view/item-view.module').then(
            m => m.ItemViewModule,
          ),
      },
      {
        path: 'deep_linking_request/:lti1p3LaunchId',
        loadChildren: () =>
          import(
            'app/lti/lti-content-selection/lti-content-selection.module'
          ).then(m => m.LtiContentSelectionModule),
      },
      {
        path: 'resource_linking_request/:lti1p3LaunchId',
        loadChildren: () =>
          import(
            '®app/lti/lti-student-list-suggestions/lti-student-list-suggestions.module'
          ).then(m => m.LtiStudentListSuggestionsModule),
      },
      {
        path: 'resource_link_content_selection/:lti1p3LaunchId',
        loadChildren: () =>
          import(
            'app/lti/lti-resource-link-content-selection/lti-resource-link-content-selection.module'
          ).then(m => m.LtiResourceLinkContentSelectionModule),
      },
      {
        path: 'deep_link_relinking',
        loadChildren: () =>
          import(
            'app/lti/lti-deep-link-relinking/lti-deep-link-relinking.module'
          ).then(m => m.LtiDeepLinkRelinkingModule),
      },
      {
        path: 'my_profile',
        canActivateChild: [TenantGuard],
        resolve: {
          tenant: TenantGuard,
        },
        loadChildren: () =>
          import('app/user-profile/user-profile.module').then(
            m => m.UserProfileModule,
          ),
      },
      {
        path: 'branding.html',
        redirectTo: 'settings/branding.html',
      },
      {
        path: 'settings.html',
        redirectTo: 'settings',
      },
      {
        path: 'settings',
        canActivateChild: [TenantGuard],
        resolve: {
          tenant: TenantGuard,
        },
        loadChildren: () =>
          import('app/settings/settings.module').then(m => m.SettingsModule),
      },
      {
        path: 'resources/:resourceId',
        canActivateChild: [TenantGuard],
        resolve: {
          tenant: TenantGuard,
        },
        loadChildren: () =>
          import('app/resource/resource.module').then(m => m.ResourceModule),
      },
      {
        path: 'oclc-wms',
        canActivateChild: [TenantGuard],
        resolve: {
          tenant: TenantGuard,
        },
        loadChildren: () =>
          import('app/oclc-wms/acquisitions/oclc-wms-acquisitions.module').then(
            m => m.OclcWmsAcquisitionsModule,
          ),
      },
      {
        path: 'bookmarks',
        canActivateChild: [TenantGuard],
        resolve: {
          tenant: TenantGuard,
        },
        loadChildren: () =>
          import('app/bookmarks/bookmarks.module').then(m => m.BookmarksModule),
      },
      {
        path: 'users/:userId',
        canActivateChild: [TenantGuard],
        resolve: {
          tenant: TenantGuard,
        },
        children: [
          {
            path: 'bookmarks/:bookmarkId',
            loadChildren: () =>
              import('app/bookmarked-resource/bookmarked-resource.module').then(
                m => m.BookmarkedResourceModule,
              ),
          },
        ],
      },
    ],
  },
  // Since our URLs will always have a list identified, our base path
  // makes no sense in isolation.
  {
    path: '**',
    pathMatch: 'full',
    component: PageNotFoundComponent,
  },
];

export const routing: ModuleWithProviders<RouterModule> =
  RouterModule.forRoot(routes);
